import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { useApi } from '../../../../../hooks/useApi';
import { ActionButtons } from '../../../../../globalComponents/modal/ActionButtons';
import { useCompanyDispatch, useCompanyState } from '../../../../../stateProviders/CompanyStateProvider';
import { Modal } from '../../../../../globalComponents/modal/Modal';

interface AddDesignPackageModalProps {
    gridRef: React.MutableRefObject<any>;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AddDesignPackageModal: React.FC<AddDesignPackageModalProps> = ({
    gridRef,
    modalOpen,
    setModalOpen,
}) => {
    const api = useApi();
    const companyDispatch = useCompanyDispatch();
    const companyState = useCompanyState();
    const [pkg, setPkg] = useState({
        packageName: '',
        packagePrice: '',
        noPermitDiscount: '0',
    });

    const resetForm = () => {
        setPkg({ packageName: '', packagePrice: '', noPermitDiscount: '0' });
    };

    useEffect(() => {
        if (modalOpen) resetForm();
    }, [modalOpen]);

    const price = Number(pkg.packagePrice);
    const discount = Number(pkg.noPermitDiscount);
    const isValidPrice = pkg.packagePrice !== '' && !isNaN(price) && price > 0;
    const isValidDiscount = !isNaN(discount) && discount >= 0 && discount <= price;

    return (
        <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            modalTitle={
                <Typography variant="h2" fontWeight="bold">
                    Add Design Package
                </Typography>
            }
            modalBody={
                <Stack spacing={2} sx={{ mb: 2 }}>
                    <Stack>
                        <FormLabel component="legend" sx={{ mb: 1 }} required>
                            Package Name
                        </FormLabel>
                        <TextField
                            fullWidth
                            name="packageName"
                            type="text"
                            placeholder="Example: Gold Package"
                            value={pkg.packageName}
                            onChange={(e) => setPkg({ ...pkg, packageName: e.target.value })}
                        />
                    </Stack>
                    <Stack>
                        <FormLabel component="legend" sx={{ mb: 1 }} required>
                            Package Price ($)
                        </FormLabel>
                        <TextField
                            fullWidth
                            name="packagePrice"
                            type="number"
                            value={pkg.packagePrice}
                            onChange={(e) => setPkg({ ...pkg, packagePrice: e.target.value })}
                            slotProps={{ htmlInput: { min: 0.01, step: 0.01 } }}
                        />
                    </Stack>
                    <Stack>
                        <FormLabel component="legend" sx={{ mb: 1 }}>
                            No Permit Discount ($)
                        </FormLabel>
                        <TextField
                            fullWidth
                            name="noPermitDiscount"
                            type="number"
                            value={pkg.noPermitDiscount}
                            onChange={(e) => setPkg({ ...pkg, noPermitDiscount: e.target.value })}
                            slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                            error={pkg.noPermitDiscount !== '' && !isValidDiscount}
                            helperText={
                                pkg.noPermitDiscount !== '' && !isValidDiscount
                                    ? 'Discount cannot exceed the package price or be negative'
                                    : ''
                            }
                        />
                    </Stack>
                </Stack>
            }
            modalFooter={
                <ActionButtons
                    confirmButtonText="Add"
                    confirmCallback={async () => {
                        if (!companyState.logisticCosts) return;
                        await api.logisticCosts.addDesignPackage(companyState.logisticCosts._id, {
                            packageName: pkg.packageName.trim(),
                            packagePrice: Math.round(price * 100) / 100,
                            noPermitDiscount: Math.round(discount * 100) / 100,
                        });
                        const logisticCosts = await api.logisticCosts.find({
                            _id: companyState.logisticCosts._id,
                        });
                        companyDispatch({ logisticCosts });
                        gridRef.current?.onRefresh();
                        resetForm();
                        setModalOpen(false);
                    }}
                    confirmDisabled={
                        pkg.packageName.trim() === '' ||
                        !isValidPrice ||
                        !isValidDiscount
                    }
                    confirmButtonStyle="success"
                    cancelCallback={() => setModalOpen(false)}
                />
            }
        />
    );
};
